import axios, { type AxiosResponse } from 'axios'
import { useRouter } from 'next/router'
import { BaseSyntheticEvent, useContext, useState } from 'react'
import { useForm } from 'react-hook-form'

import { type SanitySignupFormBlock } from '@data/sanity/queries/types/blocks'
import { type SignupFormValues } from '@lib/auth'
import { triggerFacebookEvent, FacebookEvent } from '@lib/facebook'
import { type ErrorMessages } from '@lib/helpers'
import { LanguageContext } from '@lib/language-context'
import { getLinkPageUrl } from '@lib/routes'
import { SiteContext } from '@lib/site-context'
import { StringsContext } from '@lib/strings-context'

import Alert from '@components/alert'
import Button, { ButtonVariant } from '@components/buttons/button'
import ComplexPortableText from '@components/complex-portable-text'
import InputField from '@components/input-field'

interface SignupResponse {
  error?: string
  fieldErrors?: Record<string, string>
}

type SignupFormProps = Pick<SanitySignupFormBlock, 'signupFormStrings'> & {
  className?: string
}

const SignupForm = ({ signupFormStrings, className }: SignupFormProps) => {
  const { locale } = useContext(LanguageContext)
  const strings = useContext(StringsContext)
  const { settings } = useContext(SiteContext)

  const router = useRouter()

  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<SignupFormValues>()

  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({})

  const onSubmit = async (
    values: SignupFormValues,
    event?: BaseSyntheticEvent,
  ) => {
    event?.preventDefault()

    setIsLoading(true)
    setIsError(false)
    setErrorMessages({})

    try {
      // Create user
      const signupResponse = await axios.post<
        SignupResponse,
        AxiosResponse<SignupResponse>,
        SignupFormValues
      >('/api/auth/signup', values, {
        headers: {
          'Content-Type': 'application/json',
          'X-Locale': locale,
        },
      })

      if (signupResponse.data.fieldErrors) {
        setErrorMessages(signupResponse.data.fieldErrors)
        setIsLoading(false)
        return
      }
    } catch (error) {
      console.log(error)
      setIsLoading(false)
      setIsError(true)
      return
    }

    if (settings?.facebookEvents) {
      await triggerFacebookEvent(FacebookEvent.COMPLETE_REGISTRATION, locale)
    }

    reset()
    setIsLoading(false)

    // Redirect to login page
    const url = getLinkPageUrl('loginPage')
    router.push(url, url, {
      locale,
    })
  }

  const firstNameRegister = register('firstName', {
    required: signupFormStrings.signupFirstNameMissing,
  })
  const lastNameRegister = register('lastName', {
    required: signupFormStrings.signupLastNameMissing,
  })
  const emailRegister = register('email', {
    required: signupFormStrings.signupEmailMissing,
    pattern: {
      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
      message: strings.emailInvalid,
    },
  })
  const passwordRegister = register('password', {
    required: signupFormStrings.signupPasswordMissing,
  })

  const isDisabled =
    !!errors.firstName ||
    !!errors.lastName ||
    !!errors.email ||
    !!errors.password

  return (
    <form className={className} onSubmit={handleSubmit(onSubmit)}>
      <InputField
        id="signup-fist-name"
        type="text"
        formRegister={firstNameRegister}
        errorMessage={errorMessages?.firstName ?? errors.firstName?.message}
        label={signupFormStrings.signupFirstName}
        placeholder={signupFormStrings.signupFirstNamePlaceholder}
        className="mb-4"
      />

      <InputField
        id="signup-last-name"
        type="text"
        formRegister={lastNameRegister}
        errorMessage={errorMessages?.lastName ?? errors.lastName?.message}
        label={signupFormStrings.signupLastName}
        placeholder={signupFormStrings.signupLastNamePlaceholder}
        className="mb-4"
      />

      <InputField
        id="signup-email"
        type="email"
        autoComplete="email"
        formRegister={emailRegister}
        errorMessage={errorMessages?.email ?? errors.email?.message}
        label={signupFormStrings.signupEmail}
        placeholder={signupFormStrings.signupEmailPlaceholder}
        className="mb-4"
      />

      <InputField
        id="signup-password"
        type="password"
        autoComplete="off"
        formRegister={passwordRegister}
        errorMessage={errorMessages?.password ?? errors.password?.message}
        label={signupFormStrings.signupPassword}
        placeholder={signupFormStrings.signupPasswordPlaceholder}
        className="mb-4"
      />

      {(isError || errorMessages?.[0]) && (
        <div key="error" className="mt-8">
          <Alert error>
            <ComplexPortableText
              className="rc rc-alert rc-error"
              content={signupFormStrings.signupErrorMessage}
            />
          </Alert>
        </div>
      )}

      <div className="flex justify-center mt-8">
        <Button
          type="submit"
          variant={ButtonVariant.FILLED}
          disabled={isLoading || isDisabled}
          className="min-w-[260px]"
        >
          {isLoading
            ? strings.buttonSubmitting
            : signupFormStrings.signupSubmit}
        </Button>
      </div>
    </form>
  )
}

export default SignupForm
